import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Switch,
  DatePicker,
  Select,
  Row,
  Col,
  Divider,
  Typography
} from "antd";
import dayjs from "dayjs";

import {
  BANNER_TEXT_FIELDS,
  BANNER_TEXT_MAX_LENGTH,
  DISCOUNT_TYPES,
  resolveDiscountAudience
} from "../helper";

const { Paragraph, Title } = Typography;

/**
 * Validation rules for discount factors (quoted/actual).
 * The regex /^(0(\.\d+)?|1(\.0+)?)$/ ensures the factor is strictly between 0 and 1 (inclusive).
 */
const FACTOR_VALIDATION_RULES = [
  {
    required: true,
    message: "Please enter a discount factor"
  },
  {
    pattern: /^(0(\.\d+)?|1(\.0+)?)$/,
    message: "Please enter a valid factor between 0 and 1."
  }
];

const CreateEditDiscountModal = ({
  isModalOpen = false,
  isEdit = false,
  initialValues = {},
  setIsModalOpen = () => {},
  handleCreateEditDiscount = () => {},
  targetBatchOptions = [],
  targetCourseOptions = []
}) => {
  const [form] = Form.useForm();
  const discountType = Form.useWatch("discountType", form);
  const isPublic = Form.useWatch("isPublic", form);
  const alumniOnly = Form.useWatch("alumniOnly", form);
  const targetEmailDomains = Form.useWatch("targetEmailDomains", form);
  const targetBatchIds = Form.useWatch("targetBatchIds", form);
  const targetCourseIds = Form.useWatch("targetCourseIds", form);

  const isFixedPriceDiscount = discountType === DISCOUNT_TYPES.FIXED_PRICE;

  // The allowlist is edited in its own modal, so the form only knows its saved counts.
  const audience = resolveDiscountAudience({
    discountType,
    isPublic,
    alumniOnly,
    targetEmailDomains,
    targetBatchIds,
    targetCourseIds,
    allowlistEmailCount: isEdit ? initialValues?.allowlistEmailCount : 0,
    allowlistWaitlistCount: isEdit ? initialValues?.allowlistWaitlistCount : 0
  });

  // The backend accepts a FIXED_PRICE discount only when it is private.
  const handleValuesChange = (changedValues) => {
    if (changedValues.discountType === DISCOUNT_TYPES.FIXED_PRICE) {
      form.setFieldsValue({ isPublic: false });
    }
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleFormSubmit = (values) => {
    const normalizedValues = { ...values };

    // Explicitly nullify unused pricing fields to satisfy backend validation
    if (values.discountType === DISCOUNT_TYPES.FIXED_PRICE) {
      normalizedValues.quotedPriceFactor = null;
      normalizedValues.actualPriceFactor = null;
      normalizedValues.isPublic = false;
    } else {
      normalizedValues.fixedDiscountAmountINR = null;
    }

    // alumniOnly applies to both visibilities. A public alumni discount reaches every alumnus.
    normalizedValues.alumniOnly = Boolean(values.alumniOnly);

    // A public discount reaches every user, so targeting rules have no effect on it.
    if (normalizedValues.isPublic) {
      normalizedValues.targetEmailDomains = [];
      normalizedValues.targetBatchIds = [];
      normalizedValues.targetCourseIds = [];
    }

    handleCreateEditDiscount(normalizedValues);
  };

  let formInitialValues = initialValues || {};
  if (!formInitialValues.discountType) {
    formInitialValues.discountType = DISCOUNT_TYPES.PERCENTAGE;
  }

  if (formInitialValues?.validityStartTime) {
    formInitialValues.validityStartTime = dayjs(formInitialValues.validityStartTime);
  }
  if (formInitialValues?.validityEndTime) {
    formInitialValues.validityEndTime = dayjs(formInitialValues.validityEndTime);
  }

  const { Option } = Select;
  const { Item } = Form;

  return (
    <Modal
      title={isEdit ? "Edit Discount" : "Create Discount"}
      open={isModalOpen}
      footer={null}
      onOk={handleOk}
      onCancel={handleCancel}
      width={750}
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={
          isEdit
            ? formInitialValues
            : { discountType: DISCOUNT_TYPES.PERCENTAGE, isPublic: true, alumniOnly: false }
        }
        name="discount_create_edit_form"
        onFinish={handleFormSubmit}
        onValuesChange={handleValuesChange}
      >
        <Title level={5} style={{ marginTop: 0 }}>
          Basic Information
        </Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Item
              name="discountType"
              label="Discount Type"
              rules={[{ required: true, message: "Please select a discount type!" }]}
              extra={
                isFixedPriceDiscount
                  ? "Use only for the course upgrade flow. Upgrade users get it automatically through the upgrade coupon code in app config."
                  : undefined
              }
            >
              <Select disabled={isEdit}>
                <Option value={DISCOUNT_TYPES.PERCENTAGE}>Percentage (%)</Option>
                <Option value={DISCOUNT_TYPES.FIXED_PRICE}>Fixed Price (INR)</Option>
              </Select>
            </Item>
          </Col>
          <Col xs={24} sm={12}>
            <Item
              name="displayCouponCode"
              label="Coupon Code"
              rules={[{ required: true, message: "Please enter a coupon code!" }]}
            >
              <Input placeholder="TLE....!" readOnly={isEdit} />
            </Item>
          </Col>
        </Row>

        <Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter the discount name!" }]}
        >
          <Input placeholder="Some Cool Name!" readOnly={isEdit} />
        </Item>

        <Divider dashed style={{ margin: "12px 0" }} />
        <Title level={5}>Pricing & Value</Title>

        {(discountType === DISCOUNT_TYPES.PERCENTAGE || !discountType) && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Item
                name="quotedPriceFactor"
                label="Quoted Price Factor (0-1)"
                rules={FACTOR_VALIDATION_RULES}
              >
                <Input placeholder="0.1 (for 10% discount)" readOnly={isEdit} />
              </Item>
            </Col>
            <Col xs={24} sm={12}>
              <Item
                name="actualPriceFactor"
                label="Actual Price Factor (0-1)"
                rules={FACTOR_VALIDATION_RULES}
              >
                <Input placeholder="0.1 (for 10% discount)" readOnly={isEdit} />
              </Item>
            </Col>
          </Row>
        )}

        {discountType === DISCOUNT_TYPES.FIXED_PRICE && (
          <Item
            name="fixedDiscountAmountINR"
            label="Fixed Discount Amount (INR)"
            rules={[
              { required: true, message: "Please enter the fixed discount amount!" },
              { pattern: /^\d+$/, message: "Please enter a valid whole number." }
            ]}
          >
            <Input placeholder="5000" readOnly={isEdit} />
          </Item>
        )}

        <Divider dashed style={{ margin: "12px 0" }} />
        <Title level={5}>Validity & Usage Constraints</Title>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Item
              name="validityStartTime"
              label="Start Date and Time"
              rules={[{ required: true, message: "Please select the start date and time!" }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%" }}
                placeholder="Select start date and time"
              />
            </Item>
          </Col>
          <Col xs={24} sm={12}>
            <Item name="validityEndTime" label="End Date and Time">
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%" }}
                placeholder="Select end date and time"
              />
            </Item>
          </Col>
        </Row>
        <Item
          name="usageLimit"
          label="Usage Limit"
          rules={[{ pattern: /[\d]+/, message: "Please enter a valid number." }]}
        >
          <Input placeholder="Leave empty for unlimited" />
        </Item>

        <Divider dashed style={{ margin: "12px 0" }} />
        <Title level={5}>Audience & Targeting</Title>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Item
              name="isPublic"
              label="Visibility"
              valuePropName="checked"
              extra={
                isFixedPriceDiscount
                  ? "A fixed-price discount is always private."
                  : "A private discount reaches only users who match a targeting rule or the allowlist."
              }
            >
              <Switch
                checkedChildren="Public"
                unCheckedChildren="Private"
                disabled={isFixedPriceDiscount}
              />
            </Item>
          </Col>
          <Col xs={24} sm={12}>
            <Item
              name="alumniOnly"
              label="Alumni Only"
              valuePropName="checked"
              extra="Limits the discount to alumni. Works with public and private discounts."
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Item>
          </Col>
        </Row>

        <Alert
          type={audience.reachesNobody ? "warning" : "info"}
          showIcon
          style={{ marginBottom: 16 }}
          message={`Audience: ${audience.label}`}
          description={
            audience.reachesNobody
              ? "This discount reaches nobody. Add a targeting rule below, or add emails with Manage Allowlist after you save."
              : undefined
          }
        />

        {isPublic === false && (
          <>
            <Item
              name="targetEmailDomains"
              label="Target Email Domains"
              tooltip="Press enter or type comma to add multiple domains"
            >
              <Select
                mode="tags"
                tokenSeparators={[","]}
                placeholder="e.g. iiit.ac.in, iit.ac.in"
              />
            </Item>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Item name="targetBatchIds" label="Target Batches">
                  <Select
                    mode="multiple"
                    placeholder="Select Target Batches"
                    options={targetBatchOptions}
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item name="targetCourseIds" label="Target Courses">
                  <Select
                    mode="multiple"
                    placeholder="Select Target Courses"
                    options={targetCourseOptions}
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Item>
              </Col>
            </Row>
          </>
        )}

        <Divider dashed style={{ margin: "12px 0" }} />
        <Title level={5}>Sale Banner Text</Title>
        <Paragraph type="secondary">
          The banner shows only when Banner Display is on for this discount. It shows one text,
          picked by the sale state below. An empty field shows the default text, which appears in
          grey inside the field. A private discount shows its banner only to the users it targets.
        </Paragraph>

        {BANNER_TEXT_FIELDS.map(({ key, label, help, placeholder }) => (
          <Item key={key} name={["bannerTexts", key]} label={label} extra={help}>
            <Input
              placeholder={placeholder}
              maxLength={BANNER_TEXT_MAX_LENGTH}
              showCount
              allowClear
            />
          </Item>
        ))}

        <Divider style={{ margin: "16px 0" }} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit">
            {isEdit ? "Update Discount" : "Create Discount"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
export default CreateEditDiscountModal;
